import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import { Trans, useTranslation } from 'react-i18next';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { ImageCarousel } from '../../components/ImageCarousel';
import { ImageGrid } from '../../components/ImageGrid';
import { useInView } from 'react-intersection-observer';
import './index.css';

const GALLERY_IMAGES = [
  './images/gallery/gallery_1.jpg',
  './images/gallery/gallery_2.jpg',
  './images/gallery/gallery_3.jpg',
  './images/gallery/gallery_4.jpg',
  './images/gallery/gallery_5.jpg',
  './images/gallery/gallery_6.jpg',
  './images/gallery/gallery_7.jpg',
  './images/gallery/gallery_8.jpg',
];

const MAP_CENTER = { lat: 56.95489545327712, lng: 24.137682320826176 };

const APPOINTMENT_URL =
  'https://www.piearsta.lv/lv/iestazu-katalogs/zobarstniecibas-klinika-riga-dentique-1674';

const BULLET_KEYS = [
  'home.bullet1',
  'home.bullet2',
  'home.bullet3',
  'home.bullet4',
] as const;

const features = [
  {
    icon: <GroupsOutlinedIcon sx={{ fontSize: 32 }} />,
    titleKey: 'home.teamTitle',
    descKey: 'home.teamDescription',
  },
  {
    icon: <StarOutlineIcon sx={{ fontSize: 32 }} />,
    titleKey: 'home.qualityTitle',
    descKey: 'home.qualityDescription',
  },
  {
    icon: <FavoriteBorderIcon sx={{ fontSize: 32 }} />,
    titleKey: 'home.speedTitle',
    descKey: 'home.speedDescription',
  },
  {
    icon: <LocationOnOutlinedIcon sx={{ fontSize: 32 }} />,
    titleKey: 'home.accessibilityTitle',
    descKey: 'home.accessibilityDescription',
  },
];

export const Home = () => {
  const { t } = useTranslation();

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [featuresRef, featuresInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [galleryRef, galleryInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [gridRef, gridInView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [contactRef, contactInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="home-page">
      {/* Hero */}
      <section
        ref={heroRef}
        className={`hero-section fade-in ${heroInView ? 'visible' : ''}`}
      >
        <p className="hero-eyebrow">Ramonas Paulīnes Balčinas</p>
        <h1 className="hero-title">{t('home.title')}</h1>

        <div className="hero-divider" />

        <ul className="hero-bullets">
          {BULLET_KEYS.map((key) => (
            <li key={key} className="hero-bullet">
              <span className="hero-bullet-dot" aria-hidden="true" />
              <span>
                <Trans i18nKey={key} />
              </span>
            </li>
          ))}
        </ul>

        <a
          href={APPOINTMENT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hero-cta"
        >
          {t('emergencyHeader.eAppointment')}
        </a>
      </section>

      {/* Features */}
      <section
        ref={featuresRef}
        className={`features-section fade-in ${featuresInView ? 'visible' : ''}`}
      >
        {features.map(({ icon, titleKey, descKey }) => (
          <div key={titleKey} className="feature-card">
            <div className="feature-icon">{icon}</div>
            <h3 className="feature-title">{t(titleKey)}</h3>
            <p className="feature-desc">{t(descKey)}</p>
          </div>
        ))}
      </section>

      {/* Carousel + Quote */}
      <section
        ref={galleryRef}
        className={`gallery-quote-section fade-in ${galleryInView ? 'visible' : ''}`}
      >
        <div className="carousel-wrapper">
          <ImageCarousel />
        </div>

        <blockquote className="founder-quote">
          <p>"{t('home.quote')}"</p>
          <cite>{t('home.quoteAuthor')}</cite>
        </blockquote>
      </section>

      {/* Photo grid */}
      <div
        ref={gridRef}
        className={`photo-grid-section fade-in ${gridInView ? 'visible' : ''}`}
      >
        <ImageGrid images={GALLERY_IMAGES} />
      </div>

      {/* Contact preview */}
      <section
        ref={contactRef}
        className={`contact-preview-section fade-in ${contactInView ? 'visible' : ''}`}
      >
        <h2 className="contact-preview-title">{t('contacts.contactCall')}</h2>
        <p className="contact-preview-sub">{t('contacts.contactSub')}</p>

        <div className="contact-preview-cards">
          <a
            href="https://maps.google.com/?q=Matīsa+iela+46+Rīga"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card"
          >
            <PlaceOutlinedIcon sx={{ fontSize: 28 }} />
            <span>Matīsa iela 46, Rīga</span>
          </a>
          <a href="tel:+37126409932" className="contact-card">
            <PhoneIphoneOutlinedIcon sx={{ fontSize: 28 }} />
            <span>+371 26 409 932</span>
          </a>
          <a href="mailto:info@rigadentique.lv" className="contact-card">
            <MailOutlinedIcon sx={{ fontSize: 28 }} />
            <span>info@rigadentique.lv</span>
          </a>
        </div>

        <div className="home-map">
          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ''}
          >
            <GoogleMap
              mapContainerClassName="google-map-container"
              center={MAP_CENTER}
              zoom={15}
            >
              <Marker position={MAP_CENTER} />
            </GoogleMap>
          </LoadScript>
        </div>
      </section>
    </div>
  );
};
